import { useState } from 'react'
import type { Field } from './inferFields'
import { titleOf } from './inferFields'
import { RecordForm } from './RecordForm'
import { Icon } from '@/components/ui'
import { cn } from '@/lib/cn'
import styles from './fields.module.css'

interface ListEditorProps {
  fields: Field[]
  template: unknown
  items: unknown[]
  token: string
  path: string
  onChange: (next: unknown[]) => void
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/**
 * A list of records — projects, roles, certifications — with add, remove and
 * reorder.
 *
 * Items are collapsed to their title by default. A section like Projects has
 * nine entries of twenty fields each; expanded, that is a page nobody can
 * navigate. Collapsed, the list *is* the overview, and order is visible and
 * editable, which matters because these render in order on the site.
 *
 * Deleting asks first. Everything else here is recoverable by not saving;
 * deletion in a long list is the one action where the mistake is easy and
 * the loss is invisible until after the save.
 */
export const ListEditor = ({
  fields,
  template,
  items,
  token,
  path,
  onChange,
}: ListEditorProps) => {
  const [open, setOpen] = useState<number | null>(null)
  const [confirming, setConfirming] = useState<number | null>(null)

  const replace = (index: number, next: unknown) =>
    onChange(items.map((item, i) => (i === index ? next : item)))

  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index))
    setOpen(null)
    setConfirming(null)
  }

  const move = (index: number, by: number) => {
    const to = index + by
    if (to < 0 || to >= items.length) return
    const next = [...items]
    const [moved] = next.splice(index, 1)
    next.splice(to, 0, moved)
    onChange(next)
    // Follow the item being moved, rather than whatever lands in its place.
    setOpen(to)
  }

  const add = () => {
    // Structured-clone the template so items never share nested objects; two
    // projects editing the same `detail` object would be a nasty surprise.
    const blank = structuredClone(template)
    onChange([...items, blank])
    setOpen(items.length)
  }

  return (
    <div className={styles.list}>
      {items.length === 0 && (
        <p className={styles.empty}>Nothing here yet.</p>
      )}

      <ul className={styles.listItems}>
        {items.map((item, index) => {
          const isOpen = open === index
          return (
            <li key={index} className={cn(styles.item, isOpen && styles.itemOpen)}>
              <div className={styles.itemBar}>
                <button
                  type="button"
                  className={styles.itemTitle}
                  onClick={() => setOpen(isOpen ? null : index)}
                  aria-expanded={isOpen}
                >
                  <Icon name={isOpen ? 'caretUp' : 'caretDown'} size={14} />
                  <span>{titleOf(item, index)}</span>
                </button>

                <div className={styles.itemActions}>
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className={styles.iconButton}
                    aria-label="Move up"
                  >
                    <Icon name="arrowUp" size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                    className={styles.iconButton}
                    aria-label="Move down"
                  >
                    <Icon name="arrowDown" size={14} />
                  </button>

                  {confirming === index ? (
                    <>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className={cn(styles.iconButton, styles.danger)}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(null)}
                        className={styles.iconButton}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirming(index)}
                      className={styles.iconButton}
                      aria-label={`Delete ${titleOf(item, index)}`}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div className={styles.itemBody}>
                  <RecordForm
                    fields={fields}
                    value={asRecord(item)}
                    token={token}
                    path={`${path}.${index}`}
                    onChange={(next) => replace(index, next)}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <button type="button" onClick={add} className={styles.addButton}>
        <Icon name="plus" size={14} />
        Add
      </button>
    </div>
  )
}
